import React, { useState, useEffect } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import { useTheme } from "../contexts/ThemeContext";
import {
  Search,
  Filter,
  Loader,
  Grid,
  List,
  ChevronDown,
  Dog,
  Users,
  BarChart3,
  Plus,
  Edit,
  Trash2,
  RefreshCw,
} from "lucide-react";

const BreedDatabasePage = () => {
  const { themeClasses } = useTheme();
  const [breedCatalog, setBreedCatalog] = useState(null);
  const [allBreeds, setAllBreeds] = useState([]);
  const [filteredBreeds, setFilteredBreeds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedSize, setSelectedSize] = useState("");
  const [isLoadingBreeds, setIsLoadingBreeds] = useState(false);
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [showFilters, setShowFilters] = useState(false);
  const [isRebuildingCatalog, setIsRebuildingCatalog] = useState(false);
  const [showAdminActions, setShowAdminActions] = useState(false); // For demo purposes
  const [notification, setNotification] = useState(null); // { type: 'success' | 'error', message: string }

  useEffect(() => {
    loadBreedCatalog();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      searchBreeds(searchQuery);
    } else {
      setFilteredBreeds(allBreeds);
    }
  }, [searchQuery, allBreeds]);

  const loadBreedCatalog = async () => {
    setIsLoadingBreeds(true);
    try {
      const getBreedCatalogModular = httpsCallable(
        functions,
        "getBreedCatalogModular"
      );
      const result = await getBreedCatalogModular();

      if (result.data.success) {
        const catalog = result.data.catalog;
        setBreedCatalog(catalog);

        // Extract all breeds with their groups
        const breeds = [];
        Object.entries(catalog.groups).forEach(([groupKey, group]) => {
          group.breeds.forEach((breedName) => {
            breeds.push({
              name: breedName,
              group: group.name,
              groupKey: groupKey,
            });
          });
        });

        setAllBreeds(breeds);
        setFilteredBreeds(breeds);
        setNotification({
          type: "success",
          message: `Loaded ${breeds.length} breeds from catalog`,
        });
        setTimeout(() => setNotification(null), 3000);
      }
    } catch (error) {
      console.error("Error loading breed catalog:", error);
      setNotification({
        type: "error",
        message: "Failed to load breed catalog",
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsLoadingBreeds(false);
    }
  };

  const searchBreeds = async (query) => {
    if (!query.trim()) {
      setFilteredBreeds(allBreeds);
      return;
    }

    try {
      const searchBreedCatalogModular = httpsCallable(
        functions,
        "searchBreedCatalogModular"
      );
      const result = await searchBreedCatalogModular({
        query: query,
        limit: 50,
      });

      if (result.data.success) {
        const breeds = result.data.results.map((breed) => ({
          name: breed.name,
          group: breed.group,
          groupKey: breed.group_key,
        }));
        setFilteredBreeds(breeds);
      }
    } catch (error) {
      console.error("Error searching breeds:", error);
      // Fallback to local search
      const filtered = allBreeds.filter((breed) =>
        breed.name.toLowerCase().includes(query.toLowerCase())
      );
      setFilteredBreeds(filtered);
    }
  };

  const filterBreedsByGroup = (groupKey) => {
    setSelectedGroup(groupKey);
    if (!groupKey) {
      setFilteredBreeds(allBreeds);
    } else {
      const filtered = allBreeds.filter((breed) => breed.groupKey === groupKey);
      setFilteredBreeds(filtered);
    }
    setSearchQuery(""); // Clear search when filtering
  };

  const filterBreedsBySize = (size) => {
    setSelectedSize(size);
    if (!size || !breedCatalog) {
      setFilteredBreeds(allBreeds);
    } else {
      const sizeBreeds = breedCatalog.by_size[size] || [];
      const filtered = allBreeds.filter((breed) =>
        sizeBreeds.includes(breed.name)
      );
      setFilteredBreeds(filtered);
    }
    setSearchQuery(""); // Clear search when filtering
  };

  const clearFilters = () => {
    setSelectedGroup("");
    setSelectedSize("");
    setSearchQuery("");
    setFilteredBreeds(allBreeds);
  };

  const rebuildCatalog = async () => {
    setIsRebuildingCatalog(true);
    try {
      const getBreedCatalogModular = httpsCallable(
        functions,
        "getBreedCatalogModular"
      );
      const result = await getBreedCatalogModular({ rebuild: true });

      if (result.data.success) {
        const catalog = result.data.catalog;
        setBreedCatalog(catalog);

        // Extract all breeds with their groups
        const breeds = [];
        Object.entries(catalog.groups).forEach(([groupKey, group]) => {
          group.breeds.forEach((breedName) => {
            breeds.push({
              name: breedName,
              group: group.name,
              groupKey: groupKey,
            });
          });
        });

        setAllBreeds(breeds);
        setFilteredBreeds(breeds);
        setNotification({
          type: "success",
          message: "Breed catalog rebuilt successfully!",
        });
        setTimeout(() => setNotification(null), 5000);
      }
    } catch (error) {
      console.error("Error rebuilding breed catalog:", error);
      setNotification({
        type: "error",
        message: "Failed to rebuild breed catalog. Check console for details.",
      });
      setTimeout(() => setNotification(null), 5000);
    } finally {
      setIsRebuildingCatalog(false);
    }
  };

  const toggleAdminActions = () => {
    setShowAdminActions(!showAdminActions);
  };

  if (isLoadingBreeds && !breedCatalog) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader
          className={`w-8 h-8 ${themeClasses.text.accent} animate-spin`}
        />
        <span className={`ml-3 ${themeClasses.text.secondary}`}>
          Loading breed database...
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notification */}
      {notification && (
        <div
          className={`p-4 rounded-lg border ${
            notification.type === "success"
              ? `${themeClasses.bg.success} border-green-500/50 ${themeClasses.text.success}`
              : `${themeClasses.bg.error} border-red-500/50 ${themeClasses.text.error}`
          }`}
        >
          {notification.message}
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1
            className={`text-3xl font-bold ${themeClasses.text.primary} mb-2 flex items-center`}
          >
            <Search className={`w-8 h-8 mr-3 ${themeClasses.text.accent}`} />
            Breed Database
          </h1>
          <p className={themeClasses.text.secondary}>
            Comprehensive database of dog breeds with search and filtering
          </p>
          <p className={`${themeClasses.text.tertiary} text-sm mt-1`}>
            Powered by modular breed catalog functions with CRUD operations
          </p>
        </div>

        <div className="flex items-center space-x-4">
          {/* Admin Actions */}
          <div className="flex items-center space-x-2">
            <button
              onClick={toggleAdminActions}
              className={`px-3 py-2 ${themeClasses.bg.buttonSecondary} ${themeClasses.text.primary} rounded-lg border ${themeClasses.border.primary} transition-colors text-sm`}
            >
              {showAdminActions ? "Hide Admin" : "Show Admin"}
            </button>

            {showAdminActions && (
              <button
                onClick={rebuildCatalog}
                disabled={isRebuildingCatalog}
                className={`flex items-center space-x-2 px-3 py-2 ${themeClasses.bg.button} text-white rounded-lg disabled:opacity-50 transition-colors text-sm`}
              >
                <RefreshCw
                  className={`w-4 h-4 ${
                    isRebuildingCatalog ? "animate-spin" : ""
                  }`}
                />
                <span>
                  {isRebuildingCatalog ? "Rebuilding..." : "Rebuild Catalog"}
                </span>
              </button>
            )}
          </div>

          {/* Stats */}
          {breedCatalog && (
            <div className="text-right">
              <p className={`text-2xl font-bold ${themeClasses.text.accent}`}>
                {allBreeds.length}
              </p>
              <p className={`${themeClasses.text.secondary} text-sm`}>
                Total Breeds
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Statistics */}
      {breedCatalog && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
          >
            <div className="flex items-center space-x-3">
              <BarChart3 className={`w-8 h-8 ${themeClasses.text.accent}`} />
              <div>
                <p
                  className={`text-2xl font-bold ${themeClasses.text.primary}`}
                >
                  {Object.keys(breedCatalog.groups).length}
                </p>
                <p className={`${themeClasses.text.secondary} text-sm`}>
                  Breed Groups
                </p>
              </div>
            </div>
          </div>

          <div
            className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
          >
            <div className="flex items-center space-x-3">
              <Users className={`w-8 h-8 ${themeClasses.text.accent}`} />
              <div>
                <p
                  className={`text-2xl font-bold ${themeClasses.text.primary}`}
                >
                  {breedCatalog.by_size
                    ? Object.keys(breedCatalog.by_size).length
                    : 0}
                </p>
                <p className={`${themeClasses.text.secondary} text-sm`}>
                  Size Categories
                </p>
              </div>
            </div>
          </div>

          <div
            className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
          >
            <div className="flex items-center space-x-3">
              <Dog className={`w-8 h-8 ${themeClasses.text.success}`} />
              <div>
                <p
                  className={`text-2xl font-bold ${themeClasses.text.primary}`}
                >
                  {allBreeds.length}
                </p>
                <p className={`${themeClasses.text.secondary} text-sm`}>
                  Total Breeds
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
      >
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${themeClasses.text.tertiary} w-5 h-5`}
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search breeds..."
                className={`w-full pl-10 pr-4 py-2 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg ${themeClasses.text.primary} focus:ring-2 focus:${themeClasses.ring.primary} focus:border-transparent`}
              />
            </div>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center space-x-2 px-4 py-2 ${themeClasses.bg.buttonSecondary} ${themeClasses.text.primary} rounded-lg border ${themeClasses.border.primary} transition-colors`}
          >
            <Filter className="w-5 h-5" />
            <span>Filters</span>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${
                showFilters ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* View Mode Toggle */}
          <div className={`flex ${themeClasses.bg.secondary} rounded-lg p-1`}>
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded ${
                viewMode === "grid"
                  ? `${themeClasses.bg.button} text-white`
                  : `${themeClasses.text.tertiary} hover:${themeClasses.text.primary}`
              }`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded ${
                viewMode === "list"
                  ? `${themeClasses.bg.button} text-white`
                  : `${themeClasses.text.tertiary} hover:${themeClasses.text.primary}`
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Expanded Filters */}
        {showFilters && breedCatalog && (
          <div className={`mt-4 pt-4 border-t ${themeClasses.border.primary}`}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Group Filter */}
              <div>
                <label
                  className={`block text-sm font-medium ${themeClasses.text.secondary} mb-2`}
                >
                  Breed Group
                </label>
                <select
                  value={selectedGroup}
                  onChange={(e) => filterBreedsByGroup(e.target.value)}
                  className={`w-full px-3 py-2 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg ${themeClasses.text.primary} focus:ring-2 focus:${themeClasses.ring.primary} focus:border-transparent`}
                >
                  <option value="">All Groups</option>
                  {Object.entries(breedCatalog.groups).map(([key, group]) => (
                    <option key={key} value={key}>
                      {group.name} ({group.breeds.length})
                    </option>
                  ))}
                </select>
              </div>

              {/* Size Filter */}
              <div>
                <label
                  className={`block text-sm font-medium ${themeClasses.text.secondary} mb-2`}
                >
                  Size Category
                </label>
                <select
                  value={selectedSize}
                  onChange={(e) => filterBreedsBySize(e.target.value)}
                  className={`w-full px-3 py-2 ${themeClasses.bg.input} border ${themeClasses.border.primary} rounded-lg ${themeClasses.text.primary} focus:ring-2 focus:${themeClasses.ring.primary} focus:border-transparent`}
                >
                  <option value="">All Sizes</option>
                  {breedCatalog.by_size &&
                    Object.entries(breedCatalog.by_size).map(
                      ([size, breeds]) => (
                        <option key={size} value={size}>
                          {size.charAt(0).toUpperCase() + size.slice(1)} (
                          {breeds.length})
                        </option>
                      )
                    )}
                </select>
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className={`w-full px-4 py-2 ${themeClasses.bg.buttonSecondary} ${themeClasses.text.primary} rounded-lg border ${themeClasses.border.primary} transition-colors`}
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results */}
      <div
        className={`${themeClasses.bg.card} rounded-lg border ${themeClasses.border.primary} p-6`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${themeClasses.text.primary}`}>
            Breeds ({filteredBreeds.length})
          </h3>
          {(selectedGroup || selectedSize || searchQuery) && (
            <div
              className={`flex items-center space-x-2 text-sm ${themeClasses.text.secondary}`}
            >
              <span>Filtered results</span>
              {selectedGroup && (
                <span
                  className={`px-2 py-1 bg-cyan-600/20 ${themeClasses.text.accent} rounded`}
                >
                  {breedCatalog?.groups[selectedGroup]?.name}
                </span>
              )}
              {selectedSize && (
                <span className="px-2 py-1 bg-purple-600/20 text-purple-300 rounded">
                  {selectedSize}
                </span>
              )}
              {searchQuery && (
                <span
                  className={`px-2 py-1 bg-green-600/20 ${themeClasses.text.success} rounded`}
                >
                  "{searchQuery}"
                </span>
              )}
            </div>
          )}
        </div>

        {filteredBreeds.length === 0 ? (
          <div className="text-center py-12">
            <Dog
              className={`w-16 h-16 ${themeClasses.text.tertiary} mx-auto mb-4`}
            />
            <h3
              className={`text-xl font-semibold ${themeClasses.text.secondary} mb-2`}
            >
              No breeds found
            </h3>
            <p className={themeClasses.text.tertiary}>
              Try adjusting your search or filter criteria
            </p>
          </div>
        ) : (
          <div
            className={
              viewMode === "grid"
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
                : "space-y-2"
            }
          >
            {filteredBreeds.map((breed, index) => (
              <div
                key={`${breed.name}-${index}`}
                className={
                  viewMode === "grid"
                    ? `${themeClasses.bg.secondary} rounded-lg p-4 hover:${themeClasses.bg.tertiary} transition-colors`
                    : `${themeClasses.bg.secondary} rounded-lg p-3 hover:${themeClasses.bg.tertiary} transition-colors flex items-center justify-between`
                }
              >
                <div className={viewMode === "grid" ? "text-center" : "flex-1"}>
                  <h4
                    className={`font-semibold ${themeClasses.text.primary} mb-1`}
                  >
                    {breed.name}
                  </h4>
                  <p className={`text-sm ${themeClasses.text.secondary}`}>
                    {breed.group}
                  </p>
                </div>
                {viewMode === "list" && (
                  <div className="text-right">
                    <span
                      className={`text-xs ${themeClasses.text.tertiary} px-2 py-1 ${themeClasses.bg.tertiary} rounded`}
                    >
                      {breed.groupKey}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BreedDatabasePage;
